import {
  ApiResponse,
  BackendInterface,
  SentryReporterInterface,
  Tokens,
} from "@imagine/frontend-base";
import { TokenManager } from "..";

export class BackendWeb implements BackendInterface {
  private host: string;
  private sentryReporter: SentryReporterInterface;
  private credentials: RequestCredentials = "include";

  constructor(host: string, sentryReporter: SentryReporterInterface) {
    this.host = host;
    this.sentryReporter = sentryReporter;
  }

  public getHost = () => {
    return this.host;
  };

  public saveTokens = async (
    accessToken: string,
    refreshToken: string,
  ): Promise<void> => {
    TokenManager.saveTokens({ accessToken, refreshToken });
  };

  public clearTokens = async (): Promise<void> => {
    TokenManager.clearTokens();
  };

  public hasAccessToken = async (): Promise<boolean> => {
    const accessToken = TokenManager.getToken("accessToken");
    return accessToken !== null && accessToken !== undefined;
  };

  public captureException(
    error: unknown,
    data?: Record<string, unknown>,
    userData?: Record<string, unknown>,
    severity?: "fatal" | "error" | "warning" | "info" | "log" | "debug",
  ): void {
    this.sentryReporter.captureException(error, data, userData, severity);
  }

  public get = async (
    endpoint: string,
    params?: Record<string, unknown>,
    retry: number = 1,
  ): Promise<ApiResponse> => {
    try {
      const response = await this.requestGet(endpoint, params);
      if (response && response.ok === false) {
        console.error("Fetch API error:", JSON.stringify(response));
      }

      if (response === false) {
        // User needs to login
        TokenManager.clearTokens();
        return {
          success: false,
          status: 403,
          data: null,
          version: "",
        } as ApiResponse;
      }

      let body = await this.getResponseData(response);
      if (
        typeof body === "string" &&
        (body.startsWith("{") || body.startsWith("["))
      ) {
        body = JSON.parse(body);
      }
      const version = this.getVersion(response);

      //const json = await response.json();
      return {
        success: response.status >= 200 && response.status < 300,
        status: response.status,
        data: body,
        version: version,
      } as ApiResponse;
    } catch (error) {
      if (retry > 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return await this.get(endpoint, params, retry - 1);
      }
      console.error(
        "Unable to get data from " + this.host + "/" + endpoint + ": ",
        error,
      );
      this.sentryReporter.captureException(
        "Fetch API error",
        {
          Action: "Get data",
          Path: this.host + "/" + endpoint,
          Error: JSON.stringify(error),
        },
        undefined,
        "fatal",
      );

      return {
        success: false,
        status: 0,
        data: null,
        version: "",
      };
    }
  };

  public post = async (
    endpoint: string,
    data?: Record<string, unknown>,
    retry: number = 1,
  ): Promise<ApiResponse> => {
    try {
      const response = await this.requestPost(endpoint, data);

      if (response && response.ok === false) {
        console.error("Fetch API error:", JSON.stringify(response));
      }

      if (response === false) {
        // User needs to login
        TokenManager.clearTokens();
        return {
          success: false,
          status: 403,
          data: null,
          version: "",
        } as ApiResponse;
      }

      let body = await this.getResponseData(response);
      if (
        typeof body === "string" &&
        (body.startsWith("{") || body.startsWith("["))
      ) {
        body = JSON.parse(body);
      }
      const version = this.getVersion(response);
      if (response.status === 403) {
        console.warn("Permission denied to endpoint", endpoint, body);
      }
      return {
        success: response.status >= 200 && response.status < 300,
        status: response.status,
        data: body,
        version: version,
      } as ApiResponse;
    } catch (error) {
      if (retry > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return await this.post(endpoint, data, retry - 1);
      }
      console.error(
        "Unable to post data to " + this.host + "/" + endpoint + ": ",
        error,
        data,
      );
      this.sentryReporter.captureException(
        "Fetch API error",
        {
          Action: "Post data",
          Path: this.host + "/" + endpoint,
          Error: JSON.stringify(error),
        },
        undefined,
        "fatal",
      );
      return {
        success: false,
        status: 0,
        data: null,
        version: "",
      } as ApiResponse;
    }
  };

  public postFormData = async (
    endpoint: string,
    formData: FormData,
    retry = 1,
  ): Promise<ApiResponse> => {
    try {
      const response = await this.requestPostForm(endpoint, formData);

      if (response === false) {
        // User needs to login
        TokenManager.clearTokens();
        return {
          success: false,
          status: 403,
          data: null,
        } as ApiResponse;
      }

      let body = await this.getResponseData(response);
      if (
        typeof body === "string" &&
        (body.startsWith("{") || body.startsWith("["))
      ) {
        body = JSON.parse(body);
      }
      const version = this.getVersion(response);
      if (response.status === 403) {
        console.warn("Permission denied to endpoint", endpoint, body);
      }
      return {
        success: response.status >= 200 && response.status < 300,
        status: response.status,
        data: body,
        version: version,
      } as ApiResponse;
    } catch (error) {
      if (retry > 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return await this.postFormData(endpoint, formData, retry - 1);
      }
      console.error("Unable to post form data to " + endpoint + ": " + error);
      this.sentryReporter.captureException(
        error,
        { Action: "Post data", Path: this.host + "/" + endpoint },
        {},
        "fatal",
      );
      return {
        success: false,
        status: 0,
        data: null,
        version: "",
      } as ApiResponse;
    }
  };

  public put = async (
    endpoint: string,
    data?: Record<string, unknown>,
    retry: number = 1,
  ): Promise<ApiResponse> => {
    try {
      const response = await this.requestPut(endpoint, data);

      if (response && response.ok === false) {
        console.error("Fetch API error:", JSON.stringify(response));
      }

      if (response === false) {
        // User needs to login
        TokenManager.clearTokens();
        return {
          success: false,
          status: 403,
          data: null,
          version: "",
        } as ApiResponse;
      }

      let body = await this.getResponseData(response);
      if (
        typeof body === "string" &&
        (body.startsWith("{") || body.startsWith("["))
      ) {
        body = JSON.parse(body);
      }
      const version = this.getVersion(response);
      if (response.status === 403) {
        console.warn("Permission denied to endpoint", endpoint, body);
      }
      return {
        success: response.status >= 200 && response.status < 300,
        status: response.status,
        data: body,
        version: version,
      } as ApiResponse;
    } catch (error) {
      if (retry > 0) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return await this.put(endpoint, data, retry - 1);
      }
      console.error(
        "Unable to put data to " + this.host + "/" + endpoint + ": ",
        error,
        data,
      );
      this.sentryReporter.captureException(
        "Fetch API error",
        {
          Action: "Put data",
          Path: this.host + "/" + endpoint,
          Error: JSON.stringify(error),
        },
        undefined,
        "fatal",
      );
      return {
        success: false,
        status: 0,
        data: null,
        version: "",
      } as ApiResponse;
    }
  };

  public delete = async (
    endpoint: string,
    params?: Record<string, unknown>,
    retry: number = 1,
  ): Promise<ApiResponse> => {
    try {
      const response = await this.requestDelete(endpoint, params);
      if (response && response.ok === false) {
        console.error("Fetch API error:", JSON.stringify(response));
      }

      if (response === false) {
        // User needs to login
        TokenManager.clearTokens();
        return {
          success: false,
          status: 403,
          data: null,
          version: "",
        } as ApiResponse;
      }

      let body = await this.getResponseData(response);
      if (
        typeof body === "string" &&
        (body.startsWith("{") || body.startsWith("["))
      ) {
        body = JSON.parse(body);
      }
      const version = this.getVersion(response);

      return {
        success: response.status >= 200 && response.status < 300,
        status: response.status,
        data: body,
        version: version,
      } as ApiResponse;
    } catch (error) {
      if (retry > 0) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return await this.delete(endpoint, params, retry - 1);
      }
      console.error(
        "Unable to delete data at " + this.host + "/" + endpoint + ": ",
        error,
      );
      this.sentryReporter.captureException(
        "Fetch API error",
        {
          Action: "Delete data",
          Path: this.host + "/" + endpoint,
          Error: JSON.stringify(error),
        },
        undefined,
        "fatal",
      );

      return {
        success: false,
        status: 0,
        data: null,
        version: "",
      };
    }
  };

  private getQueryString = (params: Record<string, unknown> | undefined) => {
    if (params === undefined) return "";
    let queryString = Object.keys(params)
      .map((key) => key + "=" + params[key])
      .join("&");
    if (queryString) {
      queryString = "?" + queryString;
    }
    return queryString;
  };

  private getResponseData = async (response: Response) => {
    const headers = response.headers;
    try {
      if (headers.get("Content-Type") === "application/json") {
        const json = await response.json();
        return json;
      } else if (headers.get("Content-Type") === "application/pdf") {
        const blob = await response.blob();
        return blob;
      } else {
        const text = await response.text();
        return text;
      }
    } catch (error) {
      console.error("Unable to read data", error);
      console.error("Url:", response.url);
      console.error("Header content type:", headers.get("Content-Type"));
      console.error("Status:", response.status);
      this.sentryReporter.captureException(error, {
        Action: "Parse response data",
        Status: response.status,
      });
      return "";
    }
  };

  private getVersion = (response: Response) => {
    return response.headers.get("X-Api-Version");
  };

  private getRequestHeaders = () => {
    const accessToken = TokenManager.getToken("accessToken");
    if (accessToken === null || accessToken === undefined) {
      return undefined;
    } else
      return {
        headers: {
          Authorization: "Bearer " + accessToken,
        },
      };
  };

  public renewAccess = async () => {
    const refreshToken = TokenManager.getToken("refreshToken");
    if (refreshToken === null || refreshToken === undefined) {
      return false;
    }
    const data = {
      refreshToken: refreshToken,
    };
    const init: RequestInit = {
      method: "POST",
      mode: "cors",
      credentials: this.credentials,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    };
    console.log("Requesting new tokens");
    try {
      const response: Response = await fetch(
        this.host + "/api/users/refresh-access",
        init,
      );
      const body = await this.getResponseData(response);

      if (response.status === 200) {
        console.log("Saving new tokens");
        TokenManager.saveTokens(body as Tokens);
        return true;
      } else {
        console.warn("Access token failed to renew");
        this.sentryReporter.captureException("Failed to renew access token", {
          "Status code": response.status,
          Body: body,
        });
        return false;
      }
    } catch (e) {
      console.error(e);
      this.sentryReporter.captureException(e, {
        Action: "Request new access token",
      });
      return false;
    }
  };

  private requestGet = async (
    path: string,
    data?: Record<string, unknown>,
    renew?: boolean | undefined,
  ): Promise<Response | false> => {
    let init: RequestInit = {
      method: "GET",
      mode: "cors",
      credentials: this.credentials,
    };

    const headers = this.getRequestHeaders();
    if (headers !== undefined) {
      init = {
        ...init,
        ...headers,
      };
    }

    const queryParams = this.getQueryString(data);
    // console.log("Query params:", queryParams);

    console.log("GET " + this.host + "/" + path + queryParams);

    const response: Response = await fetch(
      this.host + "/" + path + queryParams,
      init,
    );
    if (response.status === 412 && renew !== true) {
      console.log("Access token expired");
      const renewed = await this.renewAccess();
      if (renewed) {
        return this.requestGet(path, data, true);
      } else return false;
    }
    return response;
  };

  private requestPost = async (
    path: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    renew?: boolean | undefined,
  ): Promise<Response | false> => {
    const init: RequestInit = {
      method: "POST",
      credentials: this.credentials,
      mode: "cors",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(data),
    };

    const headers = this.getRequestHeaders();
    if (headers !== undefined) {
      init.headers = {
        ...init.headers,
        ...headers.headers,
      };
    }
    const response = await fetch(this.host + "/" + path, init);
    if (response.status === 412 && renew !== true) {
      const renewed = await this.renewAccess();
      if (renewed) {
        return this.requestPost(path, data, true);
      } else return false;
    }
    return response;
  };

  private requestPostForm = async (
    path: string,
    formData: FormData,
    renew?: boolean | undefined,
  ): Promise<Response | false> => {
    const init: RequestInit = {
      method: "POST",
      body: formData,
    };

    const headers = this.getRequestHeaders();
    if (headers !== undefined) {
      init.headers = {
        ...init.headers,
        ...headers.headers,
      };
    }

    const response = await fetch(this.host + "/" + path, init);

    if (response.status === 412 && renew !== true) {
      const renewed = await this.renewAccess();
      if (renewed) {
        return this.requestPostForm(path, formData, true);
      } else return false;
    }
    return response;
  };

  private requestPut = async (
    path: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    renew?: boolean | undefined,
  ): Promise<Response | false> => {
    const init: RequestInit = {
      method: "PUT",
      credentials: this.credentials,
      mode: "cors",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json; charset=utf-8",
      },
      body: JSON.stringify(data),
    };

    const headers = this.getRequestHeaders();
    if (headers !== undefined) {
      init.headers = {
        ...init.headers,
        ...headers.headers,
      };
    }
    const response = await fetch(this.host + "/" + path, init);
    if (response.status === 412 && renew !== true) {
      const renewed = await this.renewAccess();
      if (renewed) {
        return this.requestPut(path, data, true);
      } else return false;
    }
    return response;
  };

  private requestDelete = async (
    path: string,
    data?: Record<string, unknown>,
    renew?: boolean | undefined,
  ): Promise<Response | false> => {
    let init: RequestInit = {
      method: "DELETE",
      mode: "cors",
      credentials: this.credentials,
    };

    const headers = this.getRequestHeaders();
    if (headers !== undefined) {
      init = {
        ...init,
        ...headers,
      };
    }

    const queryParams = this.getQueryString(data);

    console.log("DELETE " + this.host + "/" + path + queryParams);

    const response: Response = await fetch(
      this.host + "/" + path + queryParams,
      init,
    );
    if (response.status === 412 && renew !== true) {
      console.log("Access token expired");
      const renewed = await this.renewAccess();
      if (renewed) {
        return this.requestDelete(path, data, true);
      } else return false;
    }
    return response;
  };
}
